// @migration-in-progress
/**
 * Incident Export Module - PDF generation for incident reports
 * 
 * Provides WBC3 SMS-compliant incident reporting documentation
 * for accidents, marine casualties, near misses, and other incidents
 */

import { debug } from '../logging';
import {
  createPDF,
  getPageDimensions,
  setFont,
  checkPageBreak,
  addReportHeader,
  addReportFooter,
  addSectionHeading,
  addWrappedText,
  formatDate,
  formatDateTime,
  savePDF,
  handlePDFError,
  PDF_CONFIG
} from './pdfCore';

/**
 * Export individual incident report as PDF
 * @param {Object} incident - Incident record
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportIncidentReportPDF(incident, options = {}) {
  try {
    const {
      vesselName = incident.vesselName || 'Unknown Vessel',
      category = 'Unknown Category',
      vesselIMO = 'Not Specified'
    } = options;

    if (!incident) {
      alert('No incident data available to export.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth } = getPageDimensions(pdf);
    
    let currentY = margin;

    // Add header
    currentY = addReportHeader(pdf, currentY, {
      title: 'Incident Report',
      vesselName,
      category
    });

    // Add Official Number (MCA requirement)
    if (options.vessel && options.vessel.officialNumber) {
      setFont(pdf, 'body');
      pdf.text(`Official Number: ${options.vessel.officialNumber}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    // Reference number and status
    setFont(pdf, 'heading');
    pdf.text(`Reference: ${incident.referenceNumber}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight;
    
    // Status indicator
    setFont(pdf, 'body');
    pdf.text(`Status: ${incident.status}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight + 5;

    // Basic Information Section
    currentY = addSectionHeading(pdf, currentY, 'Basic Information', 'subheading');
    
    setFont(pdf, 'body');
    const basicInfo = [
      ['Type:', incident.type],
      ['Date/Time:', formatDateTime(incident.date)],
      ['Location:', incident.location || 'Not specified'],
      ['Reported By:', incident.reportedBy],
      ['Severity:', incident.severity]
    ];

    basicInfo.forEach(([label, value]) => {
      pdf.setFont('helvetica', 'bold');
      pdf.text(label, margin + 5, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.text(value, margin + 60, currentY);
      currentY += PDF_CONFIG.lineHeight;
    });

    currentY += 5;

    // Description
    currentY = addSectionHeading(pdf, currentY, 'Description', 'subheading');
    currentY = addWrappedText(pdf, incident.description, margin + 5, currentY, contentWidth - 10);
    currentY += 5;

    // Type-specific details
    if (incident.type === 'Accident' && incident.personnelInjured) {
      currentY = checkPageBreak(pdf, currentY).currentY;
      currentY = addSectionHeading(pdf, currentY, 'Accident Details', 'subheading');
      
      // Personnel injured
      if (incident.personnelInjured && incident.personnelInjured.length > 0) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Personnel Injured:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        
        incident.personnelInjured.forEach((person, index) => {
          setFont(pdf, 'body');
          pdf.text(`${index + 1}. ${person.name} (${person.role})`, margin + 10, currentY);
          currentY += PDF_CONFIG.lineHeight;
          if (person.injuryDescription) {
            currentY = addWrappedText(pdf, `   Injury: ${person.injuryDescription}`, margin + 15, currentY, contentWidth - 20);
          }
        });
        currentY += 5;
      }

      // Medical details
      if (incident.firstAid) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('First Aid Given:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, incident.firstAid, margin + 10, currentY, contentWidth - 15);
        currentY += 5;
      }

      if (incident.medicalTreatment) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Medical Treatment:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, incident.medicalTreatment, margin + 10, currentY, contentWidth - 15);
        currentY += 5;
      }

      if (incident.timeOffWork) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Time Off Work:', margin + 5, currentY);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${incident.timeOffWork} days`, margin + 70, currentY);
        currentY += PDF_CONFIG.lineHeight + 5;
      }
    }

    if (incident.type === 'Marine Casualty') {
      currentY = checkPageBreak(pdf, currentY).currentY;
      currentY = addSectionHeading(pdf, currentY, 'Marine Casualty Details', 'subheading');
      
      if (incident.damageDescription) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Damage Description:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, incident.damageDescription, margin + 10, currentY, contentWidth - 15);
        currentY += 5;
      }

      if (incident.equipmentAffected && incident.equipmentAffected.length > 0) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Equipment Affected:', margin + 5, currentY);
        pdf.setFont('helvetica', 'normal');
        pdf.text(incident.equipmentAffected.join(', '), margin + 75, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }

      if (incident.repairCost) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Estimated Repair Cost:', margin + 5, currentY);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`£${incident.repairCost.toFixed(2)}`, margin + 85, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }

      if (incident.operationalImpact) {
        currentY += 5;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Operational Impact:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, incident.operationalImpact, margin + 10, currentY, contentWidth - 15);
        currentY += 5;
      }
    }

    if (incident.type === 'Environmental Incident' || incident.type === 'Pollution Event') {
      currentY = checkPageBreak(pdf, currentY).currentY;
      currentY = addSectionHeading(pdf, currentY, 'Environmental Details', 'subheading');
      
      const envDetails = [
        ['Substance:', incident.substanceInvolved],
        ['Quantity:', `${incident.quantityReleased} ${incident.quantityUnit}`]
      ];

      envDetails.forEach(([label, value]) => {
        if (value) {
          pdf.setFont('helvetica', 'bold');
          pdf.text(label, margin + 5, currentY);
          pdf.setFont('helvetica', 'normal');
          pdf.text(value, margin + 60, currentY);
          currentY += PDF_CONFIG.lineHeight;
        }
      });

      if (incident.containmentMeasures) {
        currentY += 5;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Containment Measures:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, incident.containmentMeasures, margin + 10, currentY, contentWidth - 15);
        currentY += 5;
      }

      if (incident.environmentalImpact) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Environmental Impact:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, incident.environmentalImpact, margin + 10, currentY, contentWidth - 15);
        currentY += 5;
      }
    }

    // Additional Information
    currentY = checkPageBreak(pdf, currentY).currentY;
    currentY = addSectionHeading(pdf, currentY, 'Additional Information', 'subheading');

    const additionalInfo = [
      ['Weather Conditions:', incident.weatherConditions],
      ['Sea State:', incident.seaState]
    ];

    additionalInfo.forEach(([label, value]) => {
      if (value) {
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, margin + 5, currentY);
        pdf.setFont('helvetica', 'normal');
        pdf.text(value, margin + 75, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }
    });

    currentY += 5;

    // Text sections
    const textSections = [
      ['Contributing Factors', incident.contributingFactors],
      ['Immediate Actions Taken', incident.immediateActions],
      ['Root Cause Analysis', incident.rootCause],
      ['Corrective Actions', incident.correctiveActions]
    ];

    textSections.forEach(([title, content]) => {
      if (content) {
        currentY = checkPageBreak(pdf, currentY).currentY;
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${title}:`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, content, margin + 10, currentY, contentWidth - 15);
        currentY += 10;
      }
    });

    // Follow-up information
    if (incident.followUpRequired) {
      currentY = checkPageBreak(pdf, currentY).currentY;
      currentY = addSectionHeading(pdf, currentY, 'Follow-up Information', 'subheading');
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Follow-up Required: Yes', margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
      
      if (incident.followUpActionsRequired) {
        currentY += 5;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Actions Required:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, incident.followUpActionsRequired, margin + 10, currentY, contentWidth - 15);
      }
      
      if (incident.followUpActionsTaken) {
        currentY += 5;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Actions Taken:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, incident.followUpActionsTaken, margin + 10, currentY, contentWidth - 15);
      }
      
      if (incident.followUpComplete !== undefined) {
        currentY += 5;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Follow-up Status:', margin + 5, currentY);
        pdf.setFont('helvetica', 'normal');
        pdf.text(incident.followUpComplete ? 'Complete' : 'Pending', margin + 80, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }
      
      currentY += 5;
    }

    if (incident.witnesses && incident.witnesses.length > 0) {
      currentY += 5;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Witnesses:', margin + 5, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.text(incident.witnesses.join(', '), margin + 60, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    // Metadata
    currentY = checkPageBreak(pdf, currentY, null, 40).currentY;
    currentY += 10;
    
    pdf.setDrawColor(200);
    pdf.line(margin, currentY, pdf.internal.pageSize.width - margin, currentY);
    currentY += 5;
    
    setFont(pdf, 'small');
    pdf.setFont('helvetica', 'italic');
    pdf.text(`Created: ${formatDateTime(incident.createdAt)}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight - 2;
    pdf.text(`Last Updated: ${formatDateTime(incident.updatedAt)} by ${incident.updatedBy}`, margin, currentY);
    
    // Add footer to all pages
    addReportFooter(pdf);

    // Save the PDF
    const fileName = `Incident_Report_${incident.referenceNumber}_${formatDate(new Date())}.pdf`;
    savePDF(pdf, fileName);

    debug.log(`Incident report PDF generated: ${fileName}`);
    return true;

  } catch (error) {
    handlePDFError(error, 'generating incident report');
    return false;
  }
}

/**
 * Export multiple incident reports as batch PDF
 * @param {Array} incidents - Array of incident records
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportIncidentBatchPDF(incidents, options = {}) {
  try {
    const {
      vesselName = 'Unknown Vessel',
      category = 'Unknown Category',
      dateRange = null
    } = options;

    if (!incidents || incidents.length === 0) {
      alert('No incidents available to export.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth } = getPageDimensions(pdf);
    
    let currentY = margin;

    // Add header
    currentY = addReportHeader(pdf, currentY, {
      title: 'Incident Reports Summary',
      vesselName,
      category
    });

    // Date range
    if (dateRange) {
      setFont(pdf, 'body');
      pdf.text(`Period: ${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight + 5;
    }

    // Summary statistics
    currentY = addSectionHeading(pdf, currentY, 'Summary', 'heading');
    
    const stats = {
      total: incidents.length,
      open: incidents.filter(i => i.status === 'Open').length,
      investigating: incidents.filter(i => i.status === 'Under Investigation').length,
      closed: incidents.filter(i => i.status === 'Closed').length,
      critical: incidents.filter(i => i.severity === 'Critical').length,
      high: incidents.filter(i => i.severity === 'High').length
    };

    setFont(pdf, 'body');
    pdf.text(`Total Incidents: ${stats.total}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`Open: ${stats.open} | Under Investigation: ${stats.investigating} | Closed: ${stats.closed}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`Critical Severity: ${stats.critical} | High Severity: ${stats.high}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight + 10;

    // Incident list
    currentY = addSectionHeading(pdf, currentY, 'Incident Details', 'heading');

    // Sort by date (newest first)
    const sortedIncidents = [...incidents].sort((a, b) => new Date(b.date) - new Date(a.date));

    sortedIncidents.forEach((incident, index) => {
      // Check for page break
      const pageBreak = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
        title: 'Incident Reports Summary (continued)',
        vesselName,
        category
      }), 80);
      currentY = pageBreak.currentY;

      // Incident header
      pdf.setFillColor(245, 245, 245);
      pdf.rect(margin, currentY - 2, contentWidth, 20, 'F');
      
      setFont(pdf, 'subheading');
      pdf.text(`${incident.referenceNumber} - ${incident.type}`, margin + 5, currentY + 5);
      
      // Status badge
      const statusX = pdf.internal.pageSize.width - margin - 50;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.text(incident.status.toUpperCase(), statusX, currentY + 5);
      
      currentY += 15;

      // Incident details
      setFont(pdf, 'small');
      pdf.text(`Date: ${formatDateTime(incident.date)}`, margin + 5, currentY);
      pdf.text(`Severity: ${incident.severity}`, margin + 100, currentY);
      currentY += PDF_CONFIG.lineHeight - 2;
      
      pdf.text(`Location: ${incident.location || 'Not specified'}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight - 2;
      
      // Description (truncated)
      const description = incident.description.length > 150 
        ? incident.description.substring(0, 150) + '...'
        : incident.description;
      currentY = addWrappedText(pdf, `Description: ${description}`, margin + 5, currentY, contentWidth - 10);
      
      currentY += 10;
    });

    // Add footer to all pages
    addReportFooter(pdf);

    // Save the PDF
    const fileName = `Incident_Summary_${vesselName.replace(/\s+/g, '_')}_${formatDate(new Date())}.pdf`;
    savePDF(pdf, fileName);

    debug.log(`Incident summary PDF generated: ${fileName}`);
    return true;

  } catch (error) {
    handlePDFError(error, 'generating incident summary');
    return false;
  }
}